import type { UnsubscribeDto } from '@dbrain/shared';
import { Alert, Button } from '@dbrain/ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { api, isApiError } from '../../api/client.js';
import { PublicPage } from './PublicSite.js';

/**
 * 16c.1 (D-259): /listeden-cik?t=…, where a marketing mail's link opens, signed in or not. The button withdraws the
 * marketing consent of the person the link names, as Ayarlar does (a mail program's one click posts the same address
 * without this page). Nothing else is changed; the mails an account needs keep coming.
 */
export function UnsubscribePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const leave = useMutation({ mutationFn: () => api<UnsubscribeDto>('POST', `/api/marketing/unsubscribe?t=${encodeURIComponent(token)}`, {}) });
  const invalid = token.length < 40 || (isApiError(leave.error) && (leave.error.code === 'not_found' || leave.error.code === 'validation_failed'));
  return (
    <PublicPage title={t('b2cModels.unsubscribe.title')}>
      <section className="unsubscribe" aria-labelledby="unsubscribe-title" data-testid="unsubscribe-page">
        <h1 id="unsubscribe-title" className="unsubscribe__title">
          {leave.isSuccess ? t('b2cModels.unsubscribe.done') : invalid ? t('b2cModels.unsubscribe.invalid') : t('b2cModels.unsubscribe.title')}
        </h1>
        <p className="unsubscribe__body" role={leave.isSuccess ? 'status' : undefined} data-testid={leave.isSuccess ? 'unsubscribe-done' : invalid ? 'unsubscribe-invalid' : undefined}>
          {leave.isSuccess ? t('b2cModels.unsubscribe.doneBody') : invalid ? t('b2cModels.unsubscribe.invalidBody') : t('b2cModels.unsubscribe.body')}
        </p>
        {!leave.isSuccess && !invalid ? (
          <>
            {leave.error ? <Alert tone="error" title={t('errors.internal_error.title')} /> : null}
            <Button size="lg" busy={leave.isPending} onClick={() => leave.mutate()} data-testid="unsubscribe-confirm">
              {t('b2cModels.unsubscribe.action')}
            </Button>
          </>
        ) : null}
      </section>
    </PublicPage>
  );
}
